/* ============================================================
   MCH KARTE — NOĆNI PROGRAM · WebGL engine (Three.js r149)
   A lit, physical night-table scene: the real die-cut tuckbox,
   57 card meshes, gold dust, a spotlight cone and a foil shader
   for the golden card. karte.js owns all choreography; this file
   only builds the stage and exposes handles.
   Units: millimeters. Box 62 × 93 × 18 · cards 58 × 87.
   ============================================================ */
(function () {
  'use strict';
  if (!window.THREE) return;
  var T = window.THREE;

  var MAROON = 0x761411, MAROON_DEEP = 0x430c0a, INTERIOR = 0x270504;
  var CW = 55, CH = 82.5, CT = 0.12;          /* card w/h/half-thickness */
  var BW = 58.5, BH = 89.2, BD = 17.5;        /* real isolated-panel dims */

  /* ---------- tiny helpers ---------- */
  /* Decode an <img> from an already-safe (same-origin or blob:) URL. */
  function decodeImage(src, revoke) {
    return new Promise(function (res, rej) {
      var im = new Image();
      im.onload = function () { if (revoke) URL.revokeObjectURL(src); res(im); };
      im.onerror = function () { if (revoke) URL.revokeObjectURL(src); rej(new Error('decode ' + src)); };
      im.src = src;
    });
  }
  /* Robust cross-origin texture load for iOS WebKit.
     Primary path: fetch → blob → object URL. A blob: image is same-origin to
     the document, so drawing it to a canvas never taints it (WebGL-safe), and
     fetch transparently follows the dev server's http→https 301 redirect while
     honoring the CDN's `Access-Control-Allow-Origin: *`. Falls back to a
     crossOrigin <img> (works when same-origin / no redirect), then rejects. */
  /* A request that FAILS is handled below (every caller has a .catch that
     substitutes cardboard art). A request that STALLS is the dangerous one:
     no load event, no error event, so the promise settles neither way and
     boot()'s Promise.all hangs forever behind the fullscreen preloader.
     Deliberately generous: all ~64 loads are kicked off at once but the
     browser runs ~6 concurrently, so a queued image can legitimately sit
     idle for several seconds before it even starts. This must only catch
     true stalls — mch-karte.js owns the harder 30s boot watchdog. */
  var LOAD_TIMEOUT_MS = 20000;

  function withTimeout(p, ms, label, onTimeout) {
    return new Promise(function (res, rej) {
      var done = false;
      var t = setTimeout(function () {
        if (done) return;
        done = true;
        if (onTimeout) { try { onTimeout(); } catch (e) {} }
        rej(new Error('timeout ' + label));
      }, ms);
      p.then(
        function (v) { if (!done) { done = true; clearTimeout(t); res(v); } },
        function (e) { if (!done) { done = true; clearTimeout(t); rej(e); } }
      );
    });
  }

  function loadImage(src) {
    var ctl = (typeof AbortController === 'function') ? new AbortController() : null;
    return withTimeout(loadImageRaw(src, ctl), LOAD_TIMEOUT_MS, src, function () {
      if (ctl) ctl.abort();   /* free the socket; don't leave it hanging */
    });
  }

  function loadImageRaw(src, ctl) {
    if (typeof fetch === 'function' && typeof URL !== 'undefined' && URL.createObjectURL) {
      return fetch(src, { mode: 'cors', credentials: 'omit', signal: ctl ? ctl.signal : undefined })
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.blob(); })
        .then(function (blob) { return decodeImage(URL.createObjectURL(blob), true); })
        .catch(function () {
          return new Promise(function (res, rej) {
            var im = new Image();
            im.crossOrigin = 'anonymous';
            im.onload = function () { res(im); };
            im.onerror = function () { rej(new Error('img ' + src)); };
            im.src = src;
          });
        });
    }
    return new Promise(function (res, rej) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () { res(im); };
      im.onerror = function () { rej(new Error('img ' + src)); };
      im.src = src;
    });
  }
  function tex(canvasOrImg, aniso) {
    var t = new T.CanvasTexture(canvasOrImg);
    t.encoding = T.sRGBEncoding;
    t.anisotropy = aniso || 4;
    return t;
  }

  /* rounded-corner card face on a canvas (art fills the whole card) */
  function cardCanvas(img, px) {
    var w = px, h = Math.round(px * 1.5);
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d');
    var r = w * 0.055;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.arcTo(w, 0, w, h, r); ctx.arcTo(w, h, 0, h, r);
    ctx.arcTo(0, h, 0, 0, r); ctx.arcTo(0, 0, w, 0, r);
    ctx.closePath(); ctx.clip();
    ctx.drawImage(img, 0, 0, w, h);
    return c;
  }

  function glowSprite() {
    var c = document.createElement('canvas');
    c.width = c.height = 64;
    var ctx = c.getContext('2d');
    var g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(255,224,160,1)');
    g.addColorStop(0.35, 'rgba(255,196,110,.55)');
    g.addColorStop(1, 'rgba(255,180,90,0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, 64, 64);
    return tex(c, 1);
  }

  /* plain cardboard panel, optionally with the gold hairline frame the
     printed panels carry (drawn at 8 px/mm) */
  function cardboardCanvas(wMm, hMm, hairline) {
    var S = 8;
    var c = document.createElement('canvas');
    c.width = Math.max(2, Math.round(wMm * S));
    c.height = Math.max(2, Math.round(hMm * S));
    var ctx = c.getContext('2d');
    ctx.fillStyle = '#761411';
    ctx.fillRect(0, 0, c.width, c.height);
    if (hairline) {
      var inset = 1.3 * S, r = 1.0 * S;
      ctx.strokeStyle = '#f5b440';
      ctx.lineWidth = 0.4 * S;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(inset, inset, c.width - 2 * inset, c.height - 2 * inset, r);
      else ctx.rect(inset, inset, c.width - 2 * inset, c.height - 2 * inset);
      ctx.stroke();
    }
    return c;
  }

  function contactShadowTex() {
    var c = document.createElement('canvas');
    c.width = 256; c.height = 128;
    var ctx = c.getContext('2d');
    var g = ctx.createRadialGradient(128, 64, 6, 128, 64, 120);
    g.addColorStop(0, 'rgba(0,0,0,.5)');
    g.addColorStop(0.55, 'rgba(0,0,0,.22)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.save(); ctx.scale(1, 0.5);
    ctx.fillStyle = g; ctx.fillRect(0, 0, 256, 256);
    ctx.restore();
    var t = new T.CanvasTexture(c);
    return t;
  }

  /* ---------- foil shader for the golden card ---------- */
  function foilMaterial(map) {
    return new T.ShaderMaterial({
      transparent: true,
      uniforms: {
        map: { value: map },
        uTime: { value: 0 },
        uFoil: { value: 1 },
        uBoost: { value: 0 },
        uOpacity: { value: 1 }
      },
      vertexShader: [
        'varying vec2 vUv;',
        'varying vec3 vN;',
        'varying vec3 vV;',
        'void main(){',
        '  vUv = uv;',
        '  vec4 mv = modelViewMatrix * vec4(position,1.0);',
        '  vN = normalize(normalMatrix * normal);',
        '  vV = normalize(-mv.xyz);',
        '  gl_Position = projectionMatrix * mv;',
        '}'
      ].join('\n'),
      fragmentShader: [
        'uniform sampler2D map;',
        'uniform float uTime;',
        'uniform float uFoil;',
        'uniform float uBoost;',
        'uniform float uOpacity;',
        'varying vec2 vUv;',
        'varying vec3 vN;',
        'varying vec3 vV;',
        'float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453); }',
        'void main(){',
        '  vec4 base = texture2D(map, vUv);',
        '  float ndv = clamp(dot(normalize(vN), normalize(vV)), 0.0, 1.0);',
        '  float fres = pow(1.0 - ndv, 2.0);',
        /* sweeping foil bands keyed to view angle + uv diagonal */
        '  float band = sin((vUv.x + vUv.y) * 14.0 + ndv * 9.0 + uTime * 0.7);',
        '  band = smoothstep(0.15, 0.95, band);',
        /* iridescent hue drift between deep gold and champagne */
        '  vec3 gold  = vec3(1.00, 0.78, 0.36);',
        '  vec3 champ = vec3(1.00, 0.93, 0.72);',
        '  vec3 rose  = vec3(1.00, 0.62, 0.47);',
        '  vec3 irid = mix(gold, champ, band);',
        '  irid = mix(irid, rose, fres * 0.4);',
        /* sparkle — view-dependent glints */
        '  float sp = hash(floor(vUv * 210.0));',
        '  float glint = smoothstep(0.988, 1.0, sp * (0.55 + 0.45 * sin(uTime * 2.0 + sp * 40.0 + ndv * 18.0)));',
        /* luminance mask: foil rides the golden ink, not the paper */
        '  float lum = dot(base.rgb, vec3(0.299, 0.587, 0.114));',
        '  float inkMask = smoothstep(0.25, 0.75, 1.0 - abs(lum - 0.55) * 1.7);',
        '  vec3 col = base.rgb;',
        '  col += irid * (band * 0.32 + fres * 0.30) * inkMask * uFoil;',
        '  col += vec3(1.0, 0.9, 0.65) * glint * (0.85 + uBoost) * uFoil;',
        '  col += irid * uBoost * 0.25;',
        '  gl_FragColor = vec4(col, base.a * uOpacity);',
        '}'
      ].join('\n')
    });
  }

  /* ---------- engine ---------- */
  function create(opts) {
    var container = typeof opts.container === 'string' ? document.querySelector(opts.container) : opts.container;
    if (!container) return null;

    var mobile = !!opts.mobile;
    var renderer;
    try {
      renderer = new T.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    } catch (e) { return null; }
    renderer.setClearColor(0x000000, 0);
    renderer.outputEncoding = T.sRGBEncoding;
    renderer.shadowMap.enabled = !mobile;
    renderer.shadowMap.type = T.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);

    var FOV = mobile ? 37 : 31;
    var scene = new T.Scene();
    scene.fog = new T.FogExp2(0x1c0605, 0.0016);

    var camRig = new T.Group();
    var camera = new T.PerspectiveCamera(FOV, 1, 60, 1600);
    camRig.add(camera);
    scene.add(camRig);

    var g = {
      renderer: renderer, scene: scene, camera: camera, camRig: camRig,
      boxFrac: opts.boxFrac || 0.44, dist: 400, vw: 1, vh: 1, mobile: mobile,
      time: 0
    };

    /* ----- lights ----- */
    var hemi = new T.HemisphereLight(0xffe2b8, 0x2a0806, 0.55);
    scene.add(hemi);

    var key = new T.SpotLight(0xffd9a0, 1.25, 0, Math.PI / 5, 0.45, 1.2);
    key.position.set(120, 260, 210);
    key.target.position.set(0, 0, 0);
    if (!mobile) {
      key.castShadow = true;
      key.shadow.mapSize.width = 1024;
      key.shadow.mapSize.height = 1024;
      key.shadow.bias = -0.0006;
      key.shadow.camera.near = 50;
      key.shadow.camera.far = 900;
    }
    scene.add(key); scene.add(key.target);

    var rim = new T.DirectionalLight(0x8ea0ff, 0.5);
    rim.position.set(-160, 120, -180);
    scene.add(rim);

    var throat = new T.PointLight(0xffb45e, 0, 130, 2);
    scene.add(throat);

    g.lights = { hemi: hemi, key: key, rim: rim, throat: throat };

    /* ----- table (shadow catcher + light pool), faded island edges ----- */
    var tableAlpha = (function () {
      var c = document.createElement('canvas');
      c.width = 256; c.height = 256;
      var ctx = c.getContext('2d');
      var gr = ctx.createRadialGradient(128, 128, 20, 128, 128, 126);
      gr.addColorStop(0, '#fff');
      gr.addColorStop(0.55, '#fff');
      gr.addColorStop(1, '#000');
      ctx.fillStyle = gr; ctx.fillRect(0, 0, 256, 256);
      return new T.CanvasTexture(c);
    })();
    var tableMat = new T.MeshStandardMaterial({ color: 0x2c0a08, roughness: 0.95, metalness: 0, transparent: true, opacity: 1, alphaMap: tableAlpha });
    var table = new T.Mesh(new T.PlaneGeometry(1500, 1500), tableMat);
    table.rotation.x = -Math.PI / 2;
    table.position.y = -BH / 2 - 10;
    table.receiveShadow = !mobile;
    scene.add(table);
    g.table = table;

    /* soft contact blob under the box (extra grounding, esp. mobile) */
    var blob = new T.Mesh(
      new T.PlaneGeometry(200, 100),
      new T.MeshBasicMaterial({ map: contactShadowTex(), transparent: true, depthWrite: false, opacity: 0.85 })
    );
    blob.rotation.x = -Math.PI / 2;
    blob.position.y = table.position.y + 0.6;
    scene.add(blob);
    g.blob = blob;

    /* ----- box ----- */
    var boxG = new T.Group();     /* world placement */
    var boxSpin = new T.Group();  /* showcase rotation */
    boxG.add(boxSpin);
    scene.add(boxG);
    g.box = boxG; g.spin = boxSpin;

    function stdMat(map, rough) {
      return new T.MeshStandardMaterial({ map: map || null, color: map ? 0xffffff : MAROON, roughness: rough || 0.55, metalness: 0.06 });
    }

    function buildBox(faces) {
      function face(w, h, m) {
        var mesh = new T.Mesh(new T.PlaneGeometry(w, h), m);
        mesh.castShadow = !mobile; mesh.receiveShadow = false;
        return mesh;
      }
      var front = face(BW, BH, stdMat(faces.front, 0.5)); front.position.z = BD / 2;
      var back = face(BW, BH, stdMat(faces.back, 0.55)); back.position.z = -BD / 2; back.rotation.y = Math.PI;
      var left = face(BD, BH, stdMat(faces.left, 0.55)); left.position.x = -BW / 2; left.rotation.y = -Math.PI / 2;
      var right = face(BD, BH, stdMat(faces.right, 0.55)); right.position.x = BW / 2; right.rotation.y = Math.PI / 2;
      var bottom = face(BW, BD, stdMat(tex(cardboardCanvas(BW, BD, false), 2), 0.6));
      bottom.position.y = -BH / 2; bottom.rotation.x = Math.PI / 2;
      boxSpin.add(front, back, left, right, bottom);

      var inMat = new T.MeshStandardMaterial({ color: INTERIOR, roughness: 0.9, side: T.DoubleSide });
      var inFront = face(BW - 1.2, BH - 1.2, inMat); inFront.position.z = BD / 2 - 0.5; inFront.rotation.y = Math.PI;
      var inBack = face(BW - 1.2, BH - 1.2, inMat); inBack.position.z = -BD / 2 + 0.5;
      var inLeft = face(BD - 1.2, BH - 1.2, inMat); inLeft.position.x = -BW / 2 + 0.5; inLeft.rotation.y = Math.PI / 2;
      var inRight = face(BD - 1.2, BH - 1.2, inMat); inRight.position.x = BW / 2 - 0.5; inRight.rotation.y = -Math.PI / 2;
      inFront.castShadow = inBack.castShadow = inLeft.castShadow = inRight.castShadow = false;
      boxSpin.add(inFront, inBack, inLeft, inRight);

      /* flap: horizontal lid hinged at the top-BACK rim, extending forward
         flat over the mouth; opening lifts it up-and-back (negative x).
         tongue: hinged at the flap's forward edge, folds down inside the
         front wall when closed (positive x). */
      var flapPivot = new T.Group();
      flapPivot.position.set(0, BH / 2, -BD / 2);
      var flapGeo = new T.PlaneGeometry(BW, BD + 0.4);
      flapGeo.rotateX(-Math.PI / 2);                    /* lie flat (XZ) */
      flapGeo.translate(0, 0, (BD + 0.4) / 2);          /* extend forward */
      var flapMat = stdMat(tex(cardboardCanvas(BW, BD + 0.4, true), 4), 0.55);
      flapMat.side = T.DoubleSide;
      var flap = new T.Mesh(flapGeo, flapMat);
      flap.castShadow = false;
      flapPivot.add(flap);
      boxSpin.add(flapPivot);
      /* closed: flap 0 (flat over the mouth); open: -2.05 (up and back).
         No tongue mesh — it read as a slab blocking the crest mid-open. */
      flapPivot.rotation.x = 0;
      g.flap = flapPivot;
      g.tongue = new T.Group();      /* inert stub, keeps old handles safe */
      throat.position.set(0, BH / 2 - 6, 0);
      boxSpin.add(throat);
    }

    /* ----- cards ----- */
    g.cards = [];
    var geoCache = new T.PlaneGeometry(CW, CH);
    function buildCards(list, backCanvas) {
      var backTx = tex(backCanvas, 6);
      list.forEach(function (item) {
        var group = new T.Group();
        var fMat = item.gold
          ? foilMaterial(null)
          : new T.MeshStandardMaterial({ map: null, transparent: true, roughness: 0.62, metalness: 0.04 });
        var bMat = new T.MeshStandardMaterial({ map: backTx, transparent: true, roughness: 0.62, metalness: 0.04 });
        var f = new T.Mesh(geoCache, fMat); f.position.z = CT;
        var b = new T.Mesh(geoCache, bMat); b.position.z = -CT; b.rotation.y = Math.PI;
        f.castShadow = b.castShadow = false;
        f.renderOrder = b.renderOrder = g.cards.length;   /* stable painter order */
        group.add(f, b);
        group.visible = false;
        scene.add(group);
        g.cards.push({
          id: item.id, suit: item.suit, rank: item.rank, gold: !!item.gold,
          group: group, front: fMat, back: bMat, frontMesh: f, backMesh: b
        });
      });
      g.goldCard = g.cards.filter(function (c) { return c.gold; })[0] || null;
    }

    /* assign a front texture (called by the loader as images stream in) */
    g.setFace = function (card, img, px) {
      var t = tex(cardCanvas(img, px), 6);
      if (card.gold) {
        card.front.uniforms.map.value = t;
      } else {
        card.front.map = t;
      }
      card.front.needsUpdate = true;
    };

    /* ----- gold dust (drift field) ----- */
    var DUST_N = mobile ? 220 : 520;
    var dustGeo = new T.BufferGeometry();
    var pos = new Float32Array(DUST_N * 3);
    var seed = new Float32Array(DUST_N);
    for (var i = 0; i < DUST_N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 900;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 620;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 500 - 60;
      seed[i] = Math.random() * 1000;
    }
    dustGeo.setAttribute('position', new T.BufferAttribute(pos, 3));
    g.dustSeed = seed;
    g.dustBase = pos.slice();
    var dustMat = new T.PointsMaterial({
      map: glowSprite(), size: mobile ? 5.5 : 4.6, transparent: true, opacity: 0.5,
      depthWrite: false, blending: T.AdditiveBlending, sizeAttenuation: true
    });
    var dust = new T.Points(dustGeo, dustMat);
    scene.add(dust);
    g.dust = dust;

    /* ----- burst particles for the golden moment (deterministic) ----- */
    var BURST_N = mobile ? 90 : 160;
    var bGeo = new T.BufferGeometry();
    var bPos = new Float32Array(BURST_N * 3);
    bGeo.setAttribute('position', new T.BufferAttribute(bPos, 3));
    var bVel = [];
    for (var j = 0; j < BURST_N; j++) {
      var a = (j / BURST_N) * Math.PI * 2 + (j % 7) * 0.13;
      var r = 34 + (j % 13) * 9;
      var up = 20 + ((j * 37) % 100) * 0.9;
      bVel.push([Math.cos(a) * r, up, Math.sin(a) * r * 0.7]);
    }
    var bMat = new T.PointsMaterial({
      map: glowSprite(), size: 7, transparent: true, opacity: 0,
      depthWrite: false, blending: T.AdditiveBlending, sizeAttenuation: true
    });
    var burst = new T.Points(bGeo, bMat);
    burst.visible = false;
    scene.add(burst);
    g.burst = { points: burst, mat: bMat, vel: bVel, n: BURST_N, t: 0, origin: new T.Vector3(0, 0, 60) };

    /* soft additive halo used behind the court close-ups */
    var halo = new T.Mesh(
      new T.PlaneGeometry(300, 300),
      new T.MeshBasicMaterial({ map: glowSprite(), transparent: true, opacity: 0, blending: T.AdditiveBlending, depthWrite: false })
    );
    halo.visible = false;
    scene.add(halo);
    g.halo = halo;

    /* spotlight cone for the golden act (fake volumetric, alpha fades down) */
    var coneGrad = (function () {
      var c = document.createElement('canvas');
      c.width = 4; c.height = 128;
      var ctx = c.getContext('2d');
      var gr = ctx.createLinearGradient(0, 0, 0, 128);
      gr.addColorStop(0, 'rgba(255,205,130,1)');
      gr.addColorStop(0.55, 'rgba(255,190,110,.38)');
      gr.addColorStop(1, 'rgba(255,180,95,0)');
      ctx.fillStyle = gr; ctx.fillRect(0, 0, 4, 128);
      var t = new T.CanvasTexture(c);
      return t;
    })();
    var coneGeo = new T.ConeGeometry(118, 560, 48, 1, true);
    coneGeo.translate(0, -280, 0);
    var coneMat = new T.MeshBasicMaterial({
      map: coneGrad, transparent: true, opacity: 0,
      blending: T.AdditiveBlending, depthWrite: false, side: T.DoubleSide
    });
    var cone = new T.Mesh(coneGeo, coneMat);
    cone.position.set(0, 320, 40);
    cone.visible = false;
    scene.add(cone);
    g.cone = cone;

    /* ----- viewport ----- */
    function setViewport() {
      var w = container.clientWidth || 1, h = container.clientHeight || 1;
      g.vw = w; g.vh = h;
      var dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1.6 : 2);
      renderer.setPixelRatio(dpr);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      var worldVh = BH / g.boxFrac;
      g.dist = worldVh / (2 * Math.tan(FOV * Math.PI / 360));
      camera.position.set(0, 24, g.dist);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
      /* world units per css px at z=0 */
      g.wpp = (g.dist * 2 * Math.tan(FOV * Math.PI / 360)) / h;
      g.worldW = g.wpp * w;
      g.worldH = g.wpp * h;
    }
    g.setViewport = setViewport;

    /* ----- render loop (dirty-flag + continuous flags) ----- */
    var running = false, dirty = true;
    g.bob = 1;                 /* hero idle amount, choreography fades it */
    g.foilOn = 0;              /* >0 keeps foil time advancing */
    g.dustOn = 1;
    g.parallax = { x: 0, y: 0, on: false };
    g.camBase = { x: 0, y: 0 };   /* choreography-owned camera offset */
    g.invalidate = function () { dirty = true; };

    var clock = new T.Clock();
    function frame() {
      if (!running) return;
      requestAnimationFrame(frame);
      var dt = Math.min(clock.getDelta(), 0.05);
      g.time += dt;
      var t = g.time;
      var animating = false;

      if (g.bob > 0.001) {
        animating = true;
        boxSpin.position.y = Math.sin(t * 1.35) * 2.2 * g.bob;
        boxSpin.rotation.z = Math.sin(t * 0.8) * 0.014 * g.bob;
      }
      if (g.dustOn > 0.01 && dust.visible) {
        animating = true;
        var p = dust.geometry.attributes.position.array;
        for (var i = 0; i < DUST_N; i++) {
          var s = g.dustSeed[i];
          p[i * 3] = g.dustBase[i * 3] + Math.sin(t * 0.16 + s) * 14;
          p[i * 3 + 1] = g.dustBase[i * 3 + 1] + Math.sin(t * 0.11 + s * 1.7) * 18 + Math.sin(t * 0.05 + s) * 8;
          p[i * 3 + 2] = g.dustBase[i * 3 + 2] + Math.cos(t * 0.13 + s) * 10;
        }
        dust.geometry.attributes.position.needsUpdate = true;
      }
      if (g.foilOn > 0.01 && g.goldCard) {
        animating = true;
        g.goldCard.front.uniforms.uTime.value = t;
      }
      var tx = g.camBase.x + (g.parallax.on ? g.parallax.x : 0);
      var ty = g.camBase.y + (g.parallax.on ? g.parallax.y : 0);
      if (Math.abs(camRig.position.x - tx) > 0.01 || Math.abs(camRig.position.y - ty) > 0.01) {
        animating = true;
        camRig.position.x += (tx - camRig.position.x) * 0.08;
        camRig.position.y += (ty - camRig.position.y) * 0.08;
      }
      if (dirty || animating) {
        renderer.render(scene, camera);
        dirty = false;
      }
    }
    g.start = function () { if (!running) { running = true; clock.start(); requestAnimationFrame(frame); } };
    g.stop = function () { running = false; };
    g.render = function () { renderer.render(scene, camera); };

    setViewport();
    g.start();
    if (!window.__MCHGL) window.__MCHGL = g;

    /* ----- asset boot — resilient: a failed image never rejects the boot, so
       the 3D scene always renders (critical on flaky mobile networks). Box
       faces fall back to a plain cardboard panel; card faces just stay blank
       (their back still shows). ----- */
    g.boot = function (assets, onProgress) {
      var done = 0, total = 2 + (assets.cards ? assets.cards.length : 0);
      function step() { done++; if (onProgress) onProgress(Math.min(1, done / total)); }

      /* box face → texture, with a plain-panel fallback keyed to which face */
      function boxFace(url, aniso, wMm, hMm, hairline) {
        return loadImage(url)
          .then(function (img) { return tex(img, aniso); })
          .catch(function () { return tex(cardboardCanvas(wMm, hMm, hairline), 2); });
      }
      var pBox = Promise.all([
        boxFace(assets.box.front, 6, BW, BH, true),
        boxFace(assets.box.back, 6, BW, BH, true),
        boxFace(assets.box.left, 4, BD, BH, false),
        boxFace(assets.box.right, 4, BD, BH, false)
      ]).then(function (t4) {
        buildBox({ front: t4[0], back: t4[1], left: t4[2], right: t4[3] });
        step();
      });

      /* card-back canvas: real art if it loads, plain cardboard otherwise */
      var px = mobile ? 448 : 512;
      var pBack = loadImage(assets.back)
        .then(function (img) { buildCards(assets.cards, cardCanvas(img, px)); step(); })
        .catch(function () { buildCards(assets.cards, cardboardCanvas(CW, CH, true)); step(); });

      var pFaces = Promise.all([pBox, pBack]).then(function () {
        return Promise.all(assets.cards.map(function (item) {
          return loadImage(item.src).then(function (img) {
            var card = g.cards.filter(function (c) { return c.id === item.id; })[0];
            if (card) g.setFace(card, img, item.px || px);
            g.invalidate();
            step();
          }).catch(function () { step(); });
        }));
      });

      return pFaces.then(function () { g.invalidate(); return g; })
        .catch(function () { g.invalidate(); return g; });   /* never reject */
    };

    return g;
  }

  /* small idle instance for the buy section (box only) */
  function createBuy(container, boxAssets) {
    var g = create({ container: container, boxFrac: 0.6, mobile: true });
    if (!g) return null;
    g.table.visible = false;
    g.blob.position.y = -BH / 2 - 4;
    g.dust.visible = false;
    g.dustOn = 0;
    g.boot({ box: boxAssets, back: boxAssets.backCard, cards: [] }, null).then(function () {
      g.spin.rotation.set(0.1, -0.5, 0);
      g.bob = 0;
      g.render();                     /* first paint without waiting on rAF */
      var t0 = performance.now();
      (function spin() {
        if (!g._dead) requestAnimationFrame(spin);
        var t = (performance.now() - t0) / 1000;
        g.spin.rotation.y = -0.5 + Math.sin(t * 0.4) * 0.6;
        g.spin.position.y = Math.sin(t * 1.05) * 1.8;
        if (g._on) g.render();
      })();
      g._on = true;
    });
    return g;
  }

  window.MCHK3D = { create: create, createBuy: createBuy, CARD: { W: CW, H: CH, T: CT }, BOX: { W: BW, H: BH, D: BD } };
})();
